import BarrelHelpers from "../../apps/data/barrel/BarrelHelpers";
import TagFilter from "../../apps/data/dashboard/TagFilter";

import { Tag, VariantGroup } from "../api/model";
import ScreenSection from "../api/model/barrels/ScreenSection";

import ProjectAIOrganizeResult, { ProjectAIOrganizeResultStatus } from "../api/model/projectAIOrganize/ProjectAIOrganizeResult";
import { BarrelData } from "../model/Barrel";
import ProjectSection from "../model/ProjectSection";
import { PartialScreen } from "../model/Screen";
import { ProjectSectionResult, ScreenSectionResult, SectionResult } from "../model/Section";
import { VariantScreen } from "../model/VariantGroup";
import WorkflowStatus from "../model/WorkflowStatus";
import { SortTypes } from "../enums";

import BasicRecord from "./BasicRecord";
import { getTimeSlot } from "./date";
import { memoizeLastResult } from "./memoize";
import { exists } from "./object";
import { calculateMatchingScore } from "./search";
import { sortBy } from "./sortBy";
import { isLetter, uppercaseFirst } from "./string";
import { RequiredOnly } from "./UtilityTypes";

type Item = BarrelData | PartialScreen;
type ProjectSectionLike = Pick<ProjectSection, "name" | "projects" | "_id" | "type">;

function generateItemsLookup<T extends Item>(maybeItemsLookup: BasicRecord<T> | undefined, items: T[]):
    BasicRecord<T> {
    if (maybeItemsLookup) {
        return maybeItemsLookup;
    }

    const itemsLookup: BasicRecord<T> = {};

    items.forEach(item => {
        itemsLookup[item._id] = item;
    });

    return itemsLookup;
}

function createDefaultSection(
    id: string,
    items: Item[],
    itemKey: "projects" | "screens" | "componentSections"
): SectionResult {
    return {
        _id: id,
        [itemKey]: items,
        type: "default",
        name: "default"
    } as unknown as SectionResult;
}

function createTimelineSections<T extends { created: string; updated: string | null; }, U extends keyof T>(
    items: T[], itemKey: string, sortType: U
): SectionResult[] {
    const timeslots: string[] = [];
    const timeline: BasicRecord<T[]> = {};

    items.forEach(item => {
        let dateString = item[sortType] as unknown as (string | null);
        if (!dateString) {
            dateString = item[SortTypes.CREATED] as unknown as string;
        }

        const timeslot = getTimeSlot(new Date(dateString));
        let timelineItems: T[];

        if (timeslot in timeline) {
            timelineItems = timeline[timeslot];
        } else {
            timeslots.push(timeslot);
            timelineItems = [];
            timeline[timeslot] = timelineItems;
        }

        timelineItems.push(item);
    }, {});

    return timeslots.map(timeslot => ({
        _id: timeslot,
        key: timeslot,
        name: uppercaseFirst(timeslot),
        actionsDisabled: true,
        [itemKey]: timeline[timeslot],
        type: "timeline"
    } as unknown as SectionResult));
}

function createNameSections<T extends { name: string; }>(items: T[], itemKey: string): SectionResult[] {
    const letterslots: string[] = [];
    const NON_ALPHABET_SLOT = "#";

    const groupedItems = items.reduce<BasicRecord<T[]>>((acc, cur) => {
        const firstChar = cur.name.substr(0, 1);

        const letterslot = isLetter(firstChar) ? firstChar.toUpperCase() : NON_ALPHABET_SLOT;
        if (acc[letterslot]) {
            acc[letterslot].push(cur);
        } else {
            letterslots.push(letterslot);
            acc[letterslot] = [cur];
        }

        return acc;
    }, {});

    return letterslots.map(letterslot => ({
        _id: letterslot,
        key: letterslot,
        name: letterslot,
        actionsDisabled: true,
        [itemKey]: groupedItems[letterslot],
        type: "name"
    } as unknown as SectionResult));
}

function createStatusTagSections(items: PartialScreen[], tagGroupTags: Array<Tag>) {
    if (!tagGroupTags) {
        return [];
    }

    return tagGroupTags?.map(tag => ({
        _id: tag._id,
        key: tag._id,
        name: tag.name,
        actionsDisabled: true,
        screens: items.filter(screen => screen.tags?.includes(tag._id)),
        type: "name"
    } as unknown as ScreenSectionResult)).filter(section => section.screens.length > 0);
}

function filterSectionsByName({ sections, filterValue, subItemsKey }: {
    sections: ScreenSectionResult[];
    filterValue: string;
    subItemsKey: "screens";
}): ScreenSectionResult[];
function filterSectionsByName({ sections, filterValue, subItemsKey, additionalTargetFn }: {
    sections: ProjectSectionResult[];
    filterValue: string;
    subItemsKey: "projects";
    additionalTargetFn?: (project: BarrelData) => string;
}): ProjectSectionResult[];
function filterSectionsByName({ sections, filterValue, subItemsKey, additionalTargetFn }: {
    sections: SectionResult[];
    filterValue: string;
    subItemsKey: "screens" | "projects";
    additionalTargetFn?: (project: BarrelData) => string;
}) {
    return sections.map(section => {
        const doesSectionMatch = calculateMatchingScore({
            searchTerm: filterValue,
            target: section.name,
            enableWordSearch: true
        });

        if (doesSectionMatch) {
            return section;
        }

        const matchedSubItems =
            (section[subItemsKey as keyof typeof section] as unknown as Item[]).filter(subItem => {
                if (!subItem.name) {
                    return false;
                }

                const additionalTarget = additionalTargetFn?.(subItem as BarrelData);

                return calculateMatchingScore({
                    searchTerm: filterValue,
                    target: subItem.name,
                    additionalTarget,
                    enableWordSearch: true
                });
            });

        if (matchedSubItems.length) {
            return {
                ...section,
                [subItemsKey]: matchedSubItems
            };
        }

        return null;
    }).filter(exists);
}

function sortItems({
    sections, items, itemsLookup, itemKey, sortType, shouldPutResultsToDefaultSection
}: {
    sections: ProjectSectionLike[];
    items: BarrelData[];
    itemsLookup?: BasicRecord<BarrelData>;
    itemKey: "projects";
    sortType: SortTypes;
    shouldPutResultsToDefaultSection: boolean;
}): ProjectSectionResult[];
function sortItems({
    sections, items, itemKey, sortType, tagGroupTags
}: {
    sections: ScreenSection[];
    items: PartialScreen[];
    itemKey: "screens";
    sortType: SortTypes;
    tagGroupTags?: Array<Tag>;
}): ScreenSectionResult[];
function sortItems({
    sections, items, itemsLookup, itemKey, sortType, shouldPutResultsToDefaultSection = false, tagGroupTags
}: {
    sections: ProjectSectionLike[] | ScreenSection[];
    items: Item[];
    itemsLookup?: BasicRecord<BarrelData>;
    itemKey: "projects" | "screens";
    sortType: SortTypes;
    shouldPutResultsToDefaultSection?: boolean;
    tagGroupTags?: Array<Tag>;
}): SectionResult[] | undefined {
    const sortMethod = sortBy[sortType as keyof typeof sortBy] as ((a: Item, b: Item) => number) | undefined;
    if (!sortMethod) {
        return populateSectionData(sections, itemsLookup, items, itemKey);
    }

    const sortedItems = items.slice(0).sort(sortMethod);

    if (shouldPutResultsToDefaultSection) {
        return [createDefaultSection("sorted", sortedItems, itemKey)];
    }

    if (sortType === SortTypes.CREATED || sortType === SortTypes.UPDATED) {
        type InferredItem = Item & { created: string; updated: string | null; };
        return createTimelineSections(sortedItems as InferredItem[], itemKey, sortType);
    }

    if (sortType === SortTypes.NAME) {
        type InferredItem = RequiredOnly<Item, "name">;
        return createNameSections(sortedItems as InferredItem[], itemKey);
    }

    if (sortType === SortTypes.STATUS_TAGS) {
        return createStatusTagSections(sortedItems as PartialScreen[], tagGroupTags!);
    }
}

function createTagFilter(tagOrTags: string | string[]): (screen: PartialScreen) => boolean {
    if (!Array.isArray(tagOrTags)) {
        return screen => !!screen.tags?.includes(tagOrTags);
    }

    return screen => tagOrTags.reduce<boolean>((prv, tag) => prv && !!screen.tags?.includes(tag), true);
}

function isTagFilterEffective(tagFilter: TagFilter): boolean {
    return !!tagFilter.tids.length || tagFilter.untagged;
}

function applyTagFilter(sections: ScreenSectionResult[], tagFilter: TagFilter): ScreenSectionResult[] {
    if (!isTagFilterEffective(tagFilter)) {
        return sections;
    }

    let filterFn: (screen: PartialScreen) => boolean;
    if (tagFilter.tids.length) {
        filterFn = createTagFilter(tagFilter.tids);
    } else if (tagFilter.untagged) {
        filterFn = screen => !screen.tags?.length;
    }

    return sections.map(section => Object.assign(section, { screens: section.screens.filter(filterFn) }))
        .filter(section => section.screens.length);
}

function applyJiraIssueFilter(project: BarrelData, sections: ScreenSection[], jiraIssueFilter: string[]) {
    const { jira: jiraIntegration } = project.integration ?? {};

    if (!jiraIssueFilter?.length || !jiraIntegration) {
        return {
            filteredSections: sections,
            filteredScreens: project.screens!
        };
    }

    const matchingAttachments = jiraIntegration.attachments!
        .filter(attachment => jiraIssueFilter.some(issue => issue.toLowerCase() === attachment.issueKey.toLowerCase()));
    const attachmentsToSections = matchingAttachments.filter(attachment => attachment.type === "section");
    const attachmentsToScreens = matchingAttachments.filter(attachment => attachment.type === "screen");

    const screenSectionMap = createItemSectionMap(project.sections!, "screens");
    const screens = project.screens!.map(screen => ({ ...screen, sectionId: screenSectionMap[screen._id] }));

    // First, find all screens whose ids or its section ids match with at least one jira issue
    const filteredScreens = screens.filter(screen => (
        attachmentsToScreens.some(attachment => attachment.itemId === screen._id) ||
            attachmentsToSections.some(attachment => attachment.itemId === screen.sectionId)
    ));

    // Filter all sections from the found screens above. If there is a jira issue attached to an empty section,
    // include that section too.
    const filteredSections = project.sections!.filter(section => (
        filteredScreens.some(screen => screen.sectionId === section._id) ||
        (!Object.values(screenSectionMap).includes(section._id) &&
                attachmentsToSections.some(attachment => attachment.itemId === section._id)))
    ).map(section => ({
        ...section,
        screens: filteredScreens.filter(screen => screen.sectionId === section._id).map(screen => screen._id)
    }));

    return {
        filteredSections,
        filteredScreens
    };
}

function applyWorkflowStatusFilter(
    sections: ProjectSectionLike[], projects: BarrelData[], statusFilter: string | null
): {
    filteredSections: ProjectSectionLike[];
    filteredProjects: BarrelData[];
} {
    if (!statusFilter || statusFilter === WorkflowStatus.All._id) {
        return {
            filteredSections: sections,
            filteredProjects: projects
        };
    }

    const projectSectionMap = createItemSectionMap(sections, "projects");
    const filteredProjects = projects.filter(({ workflowStatus }) => workflowStatus!._id === statusFilter);
    const sectionsOfFilteredProjects = filteredProjects.reduce<BasicRecord<string[]>>((acc, curr) => {
        const sectionIdOfProject = projectSectionMap[curr._id];
        (acc[sectionIdOfProject] ??= []).push(curr._id);
        return acc;
    }, {});

    const filteredSections = sections.map(section => {
        if (sectionsOfFilteredProjects[section._id]) {
            return { ...section, projects: sectionsOfFilteredProjects[section._id], collapsed: true };
        }

        return null;
    }).filter(exists);

    return {
        filteredSections,
        filteredProjects
    };
}

type VariantGroupData = {
    variantGroupId: string;
    variantGroupName: string;
    variantName: string;
    variants: VariantScreen[];
};

function applyScreenVariantFilter(
    sections: ScreenSectionResult[],
    variantGroups: VariantGroup[]
): {
    variantGroupsMapping: BasicRecord<VariantGroupData>;
    sections: ScreenSectionResult[];
} {
    const variantGroupsMapping: BasicRecord<VariantGroupData> = {};
    const screensToExclude: BasicRecord<boolean> = {};

    // If there are no variant groups, there is no need to filter screens by variants
    if (!variantGroups.length) {
        return { variantGroupsMapping, sections };
    }

    variantGroups.forEach(variantGroup => {
        const { screens, _id, name } = variantGroup;
        let defaultScreenVariantId: string;

        screens.forEach((screen, index) => {
            const { id, variantName } = screen;

            if (index === 0) {
                defaultScreenVariantId = id;
                variantGroupsMapping[defaultScreenVariantId] = {
                    variantGroupId: _id,
                    variantGroupName: name,
                    variantName,
                    variants: []
                };
            } else {
                variantGroupsMapping[defaultScreenVariantId].variants.push(screen);
                screensToExclude[id] = true;
            }
        });
    });

    sections.forEach(section => {
        section.screens = section.screens.filter(screen => !screensToExclude[screen._id]);
    });

    return { variantGroupsMapping, sections };
}

function populateSectionData(
    sections: ProjectSectionLike[] | ScreenSection[] | undefined,
    maybeItemsLookup: BasicRecord<Item> | undefined,
    items: Item[],
    itemKey: "projects" | "screens"
): SectionResult[] {
    if (!sections?.length) {
        return [];
    }

    const itemsLookup = generateItemsLookup<Item>(maybeItemsLookup, items);

    return sections.map(section => {
        const sectionWithItems = { ...section } as unknown as SectionResult;
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        sectionWithItems[itemKey] =
            (section[itemKey as keyof typeof section] as unknown as string[])
                ?.map(itemId => itemsLookup[itemId])
                .filter(exists);

        if (!(section as { type?: string; }).type) {
            sectionWithItems.type = "user";
        }

        return sectionWithItems;
    });
}

function createItemSectionMap<U extends string>(sections: ({ _id: string; } & { [k in U]: string[]; })[], itemKey: U):
    BasicRecord {
    return sections.reduce<BasicRecord>((acc, cur) => {
        cur[itemKey].forEach(itemId => {
            acc[itemId] = cur._id;
        });
        return acc;
    }, {});
}

function filterSortProjectSections({
    sections, projects, projectsLookup, sortType, filterValue = null, statusFilter = null,
    shouldPutResultsToDefaultSection
}: {
    sections: ProjectSectionLike[];
    projects: BarrelData[];
    projectsLookup: BasicRecord<BarrelData>;
    sortType: SortTypes;
    filterValue?: string | null;
    statusFilter?: string | null;
    shouldPutResultsToDefaultSection: boolean;
}): ProjectSectionResult[] {
    const { filteredSections, filteredProjects } = applyWorkflowStatusFilter(sections, projects, statusFilter);
    let sortedSections = sortItems({
        sections: filteredSections,
        items: filteredProjects,
        itemsLookup: projectsLookup,
        itemKey: "projects",
        sortType,
        shouldPutResultsToDefaultSection
    });

    if (filterValue) {
        sortedSections = filterSectionsByName({
            sections: sortedSections,
            filterValue,
            subItemsKey: "projects"
        });
    }

    return sortedSections;
}

function getTagsOfStatusGroup(project: BarrelData, sortType:SortTypes, selectedTagGroup: string | undefined) {
    if (sortType !== SortTypes.STATUS_TAGS || !selectedTagGroup || !project.tags || !project.tagGroups) {
        return;
    }

    const tagGroup = project.tagGroups.find(tg => tg._id === selectedTagGroup);
    if (!tagGroup || !tagGroup.tags) {
        return;
    }

    const tagsMapped = project.tags.reduce((acc, current) => {
        acc[current._id] = current;
        return acc;
    }, {} as BasicRecord<Tag>);

    return tagGroup?.tags.map(tagId => tagsMapped[tagId]) ?? [];
}

type FilterSortScreensParams = {
    project: BarrelData;
    sortType: SortTypes;
    filterValue: string | undefined;
    tagFilter: TagFilter;
    jiraIssueFilter: string[];
    selectedTagGroup?: string;
    projectAIOrganizeResult?: ProjectAIOrganizeResult | undefined;
};

// We're creating the memoized function here because it's used in lots of different components with the same inputs
const filterSortScreensMemoized = memoizeLastResult(filterSortScreens);

function filterSortScreens({
    project,
    sortType,
    filterValue,
    tagFilter,
    jiraIssueFilter,
    selectedTagGroup,
    projectAIOrganizeResult
}: FilterSortScreensParams) {
    const hasProjectAIOrganizeResult = !!projectAIOrganizeResult;
    let barrelSections = [...project.sections!];
    let barrelVariantGroups = [...(project.variantGroups || [])];
    let computedAIOrganizedScreenIds: string[] = [];
    let computedAIOrganizedSectionIds: string[] = [];
    let computedAIGeneratedSectionIds: string[] = [];
    let computedAIOrganizedVariantGroupIds: string[] = [];
    let computedAIGeneratedVariantGroupIds: string[] = [];

    let computedFilterValue = filterValue;
    let computedTagFilter = tagFilter;

    if (hasProjectAIOrganizeResult) {
        computedFilterValue = "";
        computedTagFilter = { tids: [], untagged: false };
    }

    if (projectAIOrganizeResult?.status === ProjectAIOrganizeResultStatus.COMPLETED) {
        const {
            newSections,
            updatedSections,
            newVariantGroups,
            updatedVariantGroups
        } = projectAIOrganizeResult.suggestions!;
        const {
            sections: AIOrganizedSections,
            variantGroups: AIOrganizedVariantGroups,
            AIOrganizedScreenIds,
            AIOrganizedSectionIds,
            AIGeneratedSectionIds,
            AIOrganizedVariantGroupIds,
            AIGeneratedVariantGroupIds
        } = BarrelHelpers.getAIOrganizedProjectSections({
            sections: barrelSections,
            variantGroups: barrelVariantGroups,
            newSections,
            updatedSections,
            newVariantGroups,
            updatedVariantGroups
        });

        barrelSections = AIOrganizedSections;
        barrelVariantGroups = AIOrganizedVariantGroups;
        computedAIOrganizedScreenIds = AIOrganizedScreenIds;
        computedAIOrganizedSectionIds = AIOrganizedSectionIds;
        computedAIGeneratedSectionIds = AIGeneratedSectionIds;
        computedAIOrganizedVariantGroupIds = AIOrganizedVariantGroupIds;
        computedAIGeneratedVariantGroupIds = AIGeneratedVariantGroupIds;
    }

    let variantGroupsMapping: BasicRecord<VariantGroupData> = {};
    const { filteredSections, filteredScreens } = applyJiraIssueFilter(project, barrelSections, jiraIssueFilter);
    const statusTagGroupTags = getTagsOfStatusGroup(project, sortType, selectedTagGroup);

    let sortedSections = applyTagFilter(sortItems({
        sections: filteredSections,
        items: filteredScreens,
        itemKey: "screens",
        sortType,
        tagGroupTags: statusTagGroupTags
    }), computedTagFilter);

    // If the default section remains after filters are applied, mark its type as default
    if (sortedSections.length && barrelSections[0]._id === sortedSections[0]._id) {
        sortedSections[0].type = "default";
    }

    const { tids, untagged } = computedTagFilter;

    if (sortType === "sections" && !tids.length && !untagged && !jiraIssueFilter.length && !computedFilterValue) {
        const {
            sections,
            variantGroupsMapping: computedVariantGroupsMapping
        } = applyScreenVariantFilter(sortedSections, barrelVariantGroups);

        sortedSections = sections;
        variantGroupsMapping = computedVariantGroupsMapping;
    }

    if (computedFilterValue) {
        sortedSections = filterSectionsByName({
            sections: sortedSections,
            filterValue: computedFilterValue,
            subItemsKey: "screens"
        });
    }

    for (let index = 0; index < sortedSections.length; index++) {
        const { path = [] } = sortedSections[index];
        const reversedPath = [...path].reverse();

        for (const parentId of reversedPath) {
            const parentIndex = sortedSections.findIndex(sortedSection => sortedSection._id === parentId);

            if (parentIndex === -1) {
                const parent = {
                    ...(barrelSections.find(section => section._id === parentId) as unknown as ScreenSectionResult)
                };

                parent.screens = [];

                sortedSections.splice(index, 0, parent);
            }
        }
    }

    return {
        sortedSections,
        variantGroupsMapping,
        AIOrganizedScreenIds: computedAIOrganizedScreenIds,
        AIOrganizedSectionIds: computedAIOrganizedSectionIds,
        AIGeneratedSectionIds: computedAIGeneratedSectionIds,
        AIOrganizedVariantGroupIds: computedAIOrganizedVariantGroupIds,
        AIGeneratedVariantGroupIds: computedAIGeneratedVariantGroupIds
    };
}

export {
    createTimelineSections,
    createNameSections,
    createItemSectionMap,
    filterSortScreens,
    filterSortScreensMemoized,
    filterSortProjectSections,
    sortItems,
    VariantGroupData,
    FilterSortScreensParams
};
